import {
  PencilIcon,
  ClockIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  CalendarIcon,
  NoSymbolIcon,
  LockClosedIcon,
  DocumentTextIcon,
  DocumentIcon,
  HandRaisedIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { getIconByName } from '../../../utils/iconMapping';

// Fallback mapping for legacy data that doesn't have icon field
const FALLBACK_ICONS = {
  'demerit': 'ExclamationTriangleIcon',
  'warning': 'ExclamationTriangleIcon',
  'detention': 'ClockIcon',
  'homework-detention': 'BookOpenIcon',
  'friday-detention': 'CalendarDaysIcon',
  'saturday-detention': 'CalendarIcon',
  'suspension': 'NoSymbolIcon',
  'privilege-loss': 'LockClosedIcon',
  'referral': 'DocumentTextIcon',
  'contract': 'DocumentIcon',
  'restorative': 'HandRaisedIcon'
};

export default function ConsequenceTypeTag({
  type,
  onClick,
  showEditIcon = true,
  showDragHandle = false,
  dragHandleProps = null,
  variant = 'default', // 'default', 'compact', 'list'
  className = ''
}) {
  // Use left border accent style (consistent with Queue and LiveFeed)
  const borderAccentStyle = type.color ? {
    borderLeftWidth: '6px',
    borderLeftColor: type.color
  } : {};

  // Get the icon - use user-selected icon if available, otherwise fallback
  const getIcon = () => {
    // If type has an icon field that's a valid Heroicon name, use it
    if (type.icon && type.icon.endsWith('Icon')) {
      return getIconByName(type.icon, 'w-5 h-5');
    }
    // Otherwise use fallback mapping
    const fallbackIconName = FALLBACK_ICONS[type.value] || 'ExclamationTriangleIcon';
    return getIconByName(fallbackIconName, 'w-5 h-5');
  };

  return (
    <motion.button
      onClick={onClick}
      className={`
        w-full text-left transition-all duration-200
        p-3 rounded-lg border border-base-300 bg-white hover:shadow-md
        ${type.active === false ? 'opacity-50' : ''}
        ${className}
      `}
      style={borderAccentStyle}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <div className="flex items-center justify-between gap-2">
        {/* Drag Handle */}
        {showDragHandle && (
          <div
            className="cursor-grab hover:bg-base-content/5 rounded p-1 -ml-1 flex-shrink-0 self-center"
            {...dragHandleProps}
          >
            <svg
              className="w-4 h-4 opacity-40"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <circle cx="7" cy="5" r="1.5" />
              <circle cx="7" cy="10" r="1.5" />
              <circle cx="7" cy="15" r="1.5" />
              <circle cx="13" cy="5" r="1.5" />
              <circle cx="13" cy="10" r="1.5" />
              <circle cx="13" cy="15" r="1.5" />
            </svg>
          </div>
        )}

        <div className="flex-1 min-w-0 flex items-center gap-2">
          {/* Icon - Heroicons */}
          <div className="flex-shrink-0">
            {getIcon()}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium block truncate">
                {type.label}
              </span>
              {type.active === false && (
                <span className="badge badge-ghost badge-sm">Inactive</span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-xs bg-base-200 px-1.5 py-0.5 rounded">{type.value}</code>
              <span className={`badge badge-${
                type.severity === 'low' ? 'info' :
                type.severity === 'high' ? 'error' :
                'warning'
              } badge-xs`}>
                {type.severity}
              </span>
              {type.requiresAction && (
                <span className="badge badge-success badge-xs">Requires Action</span>
              )}
            </div>
            {type.description && (
              <p className="text-sm opacity-70 mt-1 line-clamp-2">
                {type.description}
              </p>
            )}
          </div>
        </div>
        {showEditIcon && (
          <PencilIcon className="w-4 h-4 opacity-40 flex-shrink-0 mt-1" />
        )}
      </div>
    </motion.button>
  );
}
